import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { media, visibility } from '~/styles/mixins';
import {
	CTARow,
	ImageBlock,
	Image640x560,
	LogoBlock,
	MultipleSrcPicture,
	Signup,
	Header,
} from '~/components/Marketing';
import ResponsiveText from '~/components/ResponsiveText';
import { Translation, useTranslation } from '~/components/Translation';
import Button from '~/components/Button';
import LinkBody from '~/components/LinkBodyV2';
import Pen from '~/components/CustomIcons/Marketing/WritingPen';
import Pulse from '~/components/CustomIcons/Marketing/Pulse';
import Heart from '~/components/CustomIcons/Marketing/Heart';
import Award from '~/components/CustomIcons/Marketing/Award';
import Key from '~/components/CustomIcons/Marketing/Key';
import Bookmark from '~/components/CustomIcons/Marketing/Bookmark';
import Star from '~/components/CustomIcons/Marketing/Star';
import Sun from '~/components/CustomIcons/Marketing/Sun';
import ThumbsUp from '~/components/CustomIcons/Marketing/ThumbsUp';
import People from '~/components/CustomIcons/Marketing/People';
import Palette from '~/components/CustomIcons/Marketing/Palette';
import Multistreaming from '~/components/CustomIcons/Marketing/Multistreaming';
import CameraPink from '~/components/CustomIcons/Marketing/Camera';
import HeartComment from '~/components/CustomIcons/Marketing/HeartComment';
import AddGuest from '~/components/CustomIcons/Marketing/AddGuest';
import analytics from '~/services/analytics';
import usePageTrack from '~/services/analytics/usePageTrack';
import { userSelectors } from '~/services/user';

import DigitialOceanPng from '~/static/images/brandLogos/digital-ocean.png';
import DigitialOceanWebp from '~/static/images/brandLogos/digital-ocean.webp';
import GoogleForStartupsPng from '~/static/images/brandLogos/google-for-startups.png';
import GoogleForStartupsWebp from '~/static/images/brandLogos/google-for-startups.webp';
import HootsuitePng from '~/static/images/brandLogos/hootsuite.png';
import HootsuiteWebp from '~/static/images/brandLogos/hootsuite.webp';
import MicrosoftPng from '~/static/images/brandLogos/microsoft.png';
import MicrosoftWebp from '~/static/images/brandLogos/microsoft.webp';
import MorningBrewPng from '~/static/images/brandLogos/morning-brew.png';
import MorningBrewWebp from '~/static/images/brandLogos/morning-brew.webp';
import SemRushPng from '~/static/images/brandLogos/semrush.png';
import SemRushWebp from '~/static/images/brandLogos/semrush.webp';
import TeachablePng from '~/static/images/brandLogos/teachable.png';
import TeachableWebp from '~/static/images/brandLogos/teachable.webp';
import WooCommercePng from '~/static/images/brandLogos/woo-commerce.png';
import WooCommerceWebp from '~/static/images/brandLogos/woo-commerce.webp';
import HeroImageSmallWebp from './hero_image_small.webp';
import HeroImageSmallPng from './hero_image_small.png';
import HeroImageLargeWebp from './hero_image_large.webp';
import HeroImageLargePng from './hero_image_large.png';
import BlockImageLiveVideoWebp from './block_image_live_video.webp';
import BlockImageLiveVideoPng from './block_image_live_video.png';
import StreamYardFeatureList from './components/StreamYardFeatureList';
import StreamYardForCard from './components/StreamYardForCard';

const HeroImageLarge = styled.div`
	width: 100%;
	max-width: 1400px;
	margin: 24px auto;
	${visibility.showMedium}
`;

const HeroImageLargeImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const HeroImageSmall = styled.div`
	width: 100%;
	max-width: 500px;
	margin: 16px auto;
	padding: 0 24px;
	${media.medium`
    	padding: 0 80px;
  	`}
	${visibility.hideMedium}
`;

const HeroImageSmallImage = styled(MultipleSrcPicture)`
	width: 100%;
`;

const StyledBlockText = styled(ResponsiveText)`
	margin-bottom: 16px;
	${media.xLarge`
      margin-bottom: 24px;
  `}
`;

const IconFeatureWrap = styled.div`
	margin: 8px 0;
	display: flex;
	justify-content: flex-start;
	align-items: center;
`;

const IconWrap = styled.div`
	width: 24px;
	margin-right: 16px;
`;

const CardWrap = styled.div`
	max-width: 700px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	${media.medium`
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
  `}
`;

const LogoImage = styled(MultipleSrcPicture)`
	width: 159px;
	${media.large`
    width: 224px;
  `}
`;

const StyledContactButton = styled(Button)`
	${media.large`
		width: fit-content;
	`}
`;

const StreamYardForCards = () => (
	<CardWrap>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.brands.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.brands" />
			}
			icon={<Award />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.influencers.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.influencers" />
			}
			icon={<Heart />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.creators.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.creators" />
			}
			icon={<Pen size="24" />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.leaders.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.leaders" />
			}
			icon={<Star />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.media.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.media" />
			}
			icon={<ThumbsUp />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.marketers.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.marketers" />
			}
			icon={<Pulse />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.entrepreneurs.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.entrepreneurs" />
			}
			icon={<Key />}
		/>
		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.politicians.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.politicians" />
			}
			icon={<Bookmark />}
		/>

		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.churches.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.churches" />
			}
			icon={<Sun />}
		/>

		<StreamYardForCard
			body={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.peopleTeams.body" />
			}
			header={
				<Translation tid="landingWhyStreamYard.card.streamyardFor.peopleTeams" />
			}
			icon={<People />}
		/>
	</CardWrap>
);

const WhyStreamYard = ({ isAuth }) => {
	usePageTrack({
		name: analytics.events.WHY_STREAMYARD,
		category: analytics.events.category.MARKETING,
	});

	return (
		<main role="main">
			<Header
				title={<Translation tid="landingWhyStreamYard.title" />}
				subtitles={[
					<Translation key="1" tid="landingWhyStreamYard.subtitle" />,
				]}
				cta={<Signup />}
			/>
			<HeroImageLarge>
				<HeroImageLargeImage
					webpSrc={HeroImageLargeWebp}
					fallbackSrc={HeroImageLargePng}
					alt=""
				/>
			</HeroImageLarge>
			<HeroImageSmall>
				<HeroImageSmallImage
					webpSrc={HeroImageSmallWebp}
					fallbackSrc={HeroImageSmallPng}
					alt=""
				/>
			</HeroImageSmall>
			<ImageBlock
				body={useTranslation(
					'landingWhyStreamYard.block.preferLiveVideo.body.one',
				)}
				bodyTwo={useTranslation(
					'landingWhyStreamYard.block.preferLiveVideo.body.two',
				)}
				header={useTranslation(
					'landingWhyStreamYard.block.preferLiveVideo.header',
				)}
				image={
					<Image640x560
						webpSrc={BlockImageLiveVideoWebp}
						fallbackSrc={BlockImageLiveVideoPng}
						alt=""
					/>
				}
				reverse={true}
			/>
			<ImageBlock color="slate" image={<StreamYardFeatureList />}>
				<Fragment>
					<StyledBlockText variant="h2">
						<Translation tid="landingWhyStreamYard.block.certainFeatures.header" />
					</StyledBlockText>
					<StyledBlockText variant="body">
						<Translation tid="landingWhyStreamYard.block.certainFeatures.subHeader" />
					</StyledBlockText>
					<IconFeatureWrap>
						<IconWrap>
							<CameraPink />
						</IconWrap>
						<ResponsiveText variant="body">
							<Translation tid="landingWhyStreamYard.block.certainFeatures.easyProduction" />
						</ResponsiveText>
					</IconFeatureWrap>
					<IconFeatureWrap>
						<IconWrap>
							<Multistreaming size="24" stroke={3} fill="#FFCC34" />
						</IconWrap>
						<ResponsiveText variant="body">
							<Translation tid="landingWhyStreamYard.block.certainFeatures.multistreaming" />
						</ResponsiveText>
					</IconFeatureWrap>
					<IconFeatureWrap>
						<IconWrap>
							<AddGuest />
						</IconWrap>
						<ResponsiveText variant="body">
							<Translation tid="landingWhyStreamYard.block.certainFeatures.guestsAndTeams" />
						</ResponsiveText>
					</IconFeatureWrap>
					<IconFeatureWrap>
						<IconWrap>
							<Palette />
						</IconWrap>
						<ResponsiveText variant="body">
							<Translation tid="landingWhyStreamYard.block.certainFeatures.onBrand" />
						</ResponsiveText>
					</IconFeatureWrap>
					<IconFeatureWrap>
						<IconWrap>
							<HeartComment />
						</IconWrap>
						<ResponsiveText variant="body">
							<Translation tid="landingWhyStreamYard.block.certainFeatures.interaction" />
						</ResponsiveText>
					</IconFeatureWrap>
				</Fragment>
			</ImageBlock>
			<ImageBlock image={<StreamYardForCards />}>
				<StyledBlockText variant="h2">
					<Translation tid="landingWhyStreamYard.block.whoIsStreamYardFor.header" />
				</StyledBlockText>
				<StyledBlockText variant="body">
					<Translation tid="landingWhyStreamYard.block.whoIsStreamYardFor.body" />
				</StyledBlockText>
				{!isAuth && (
					<StyledContactButton to="/signup">
						<Translation tid="landingWhyStreamYard.block.whoIsStreamYardFor.button" />
					</StyledContactButton>
				)}
			</ImageBlock>
			<LogoBlock
				color="white"
				header={<Translation tid="landingWhyStreamYard.logos.header" />}
				titleTag="h2"
			>
				<LogoImage
					webpSrc={MicrosoftWebp}
					fallbackSrc={MicrosoftPng}
					alt="Microsoft"
				/>
				<LogoImage
					webpSrc={GoogleForStartupsWebp}
					fallbackSrc={GoogleForStartupsPng}
					alt="Google for Startups"
				/>
				<LogoImage
					webpSrc={TeachableWebp}
					fallbackSrc={TeachablePng}
					alt="Teachable"
				/>
				<LogoImage
					webpSrc={WooCommerceWebp}
					fallbackSrc={WooCommercePng}
					alt="WooCommerce"
				/>
				<LogoImage
					webpSrc={DigitialOceanWebp}
					fallbackSrc={DigitialOceanPng}
					alt="Digital Ocean"
				/>
				<LogoImage
					webpSrc={MorningBrewWebp}
					fallbackSrc={MorningBrewPng}
					alt="Morning Brew"
				/>
				<LogoImage
					webpSrc={SemRushWebp}
					fallbackSrc={SemRushPng}
					alt="SemRush"
				/>
				<LogoImage
					webpSrc={HootsuiteWebp}
					fallbackSrc={HootsuitePng}
					alt="Hootsuite"
				/>
			</LogoBlock>
			<CTARow
				body={
					<Translation
						tid="landingWhyStreamYard.cta.body"
						components={{
							link: <LinkBody to="/contact-us" color="onPrimary" />,
						}}
					/>
				}
				title={<Translation tid="landingWhyStreamYard.cta.title" />}
				subtitle={<Translation tid="landingWhyStreamYard.cta.subtitle" />}
				cta={<Signup />}
			/>
		</main>
	);
};

WhyStreamYard.propTypes = {
	isAuth: PropTypes.bool,
	NextImage: PropTypes.elementType,
};

const mapStateToProps = state => ({
	isAuth: userSelectors.isAuth(state),
});

export default connect(mapStateToProps)(WhyStreamYard);
